import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bot,
  Layers,
  SlidersHorizontal,
  TrendingUp,
  Users,
} from "lucide-react";
import { goToFixturePredictions } from "../../utils/predictionsNavigation";

const FormDots = ({ form }) => {
  const chars = (form || "").split("").slice(0, 5);
  if (!chars.length) return null;
  return (
    <div className="fd-form-dots" aria-label={`Form: ${form}`}>
      {chars.map((c, i) => (
        <span key={`${c}-${i}`} className={`fd-form-dot fd-form-${c.toLowerCase()}`}>
          {c}
        </span>
      ))}
    </div>
  );
};

const PRED_CHIPS = [
  { key: "community", label: "Comm", Icon: Users, tone: "community" },
  { key: "ai", label: "AI", Icon: Bot, tone: "ai" },
  { key: "total", label: "Total", Icon: Layers, tone: "total" },
];

const predValue = (counts, key) => {
  if (key === "community") return counts?.communityPredictions ?? 0;
  if (key === "ai") return counts?.aiPredictions ?? 0;
  return counts?.totalPredictions ?? 0;
};

const FixtureDeskIntel = ({ item, fixtureId, houseId, isPast, hideOddsAction = false }) => {
  const navigate = useNavigate();
  const home = item.homeTeam || {};
  const away = item.awayTeam || {};
  const preds = item.predictionCounts;
  const standings = item.standings;
  const hasStandings = Boolean(standings?.home || standings?.away);
  const hasPreds = Boolean(preds);

  const goPredictions = (mode) => {
    goToFixturePredictions(navigate, { homeTeam: home, awayTeam: away, mode });
  };

  const oddsHistoryPath =
    houseId && fixtureId
      ? `/app/bethouse/manage/${houseId}/fixtures/${fixtureId}/odds-history`
      : null;
  const oddsCustomizePath =
    !isPast && houseId && fixtureId
      ? `/app/bethouse/manage/${houseId}/fixtures/${fixtureId}/odds`
      : null;
  const oddsLabel = item.oddsConfigured ? "Update odds" : "Set odds";

  return (
    <div className="fd-card-intel-wrap">
      <div className="fd-card-nav" role="group" aria-label="Fixture links">
        <button type="button" className="fd-nav-btn fd-nav-btn-predict" onClick={() => goPredictions()}>
          <TrendingUp size={13} aria-hidden />
          <span>Predictions</span>
        </button>
        {oddsCustomizePath && !hideOddsAction ? (
          <Link to={oddsCustomizePath} className="fd-nav-btn fd-nav-btn-odds">
            <SlidersHorizontal size={13} aria-hidden />
            <span>{oddsLabel}</span>
          </Link>
        ) : null}
        {oddsHistoryPath ? (
          <Link to={oddsHistoryPath} className="fd-nav-btn fd-nav-btn-history">
            <BarChart3 size={13} aria-hidden />
            <span>Odds history</span>
          </Link>
        ) : null}
      </div>

      {(hasPreds || hasStandings) && (
        <div className="fd-card-intel">
          {hasPreds ? (
            <div className="fd-intel-block fd-intel-predictions">
              <div className="fd-intel-head">
                <p className="fd-intel-label">Predictions</p>
                <button type="button" className="fd-intel-link" onClick={() => goPredictions()}>
                  View all
                </button>
              </div>
              <div className="fd-pred-grid">
                {PRED_CHIPS.map(({ key, label, Icon, tone }) => {
                  const value = predValue(preds, key);
                  const mode = key === "ai" ? "ai" : key === "community" ? "community" : undefined;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`fd-pred-chip fd-pred-chip-${tone} ${value > 0 ? "fd-pred-chip-active" : ""}`}
                      onClick={() => goPredictions(mode)}
                      title={`View ${label.toLowerCase()} predictions`}
                    >
                      <span className="fd-pred-chip-top">
                        <Icon size={9} aria-hidden />
                        <span>{label}</span>
                      </span>
                      <strong className="tabular-nums">{value}</strong>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {hasStandings ? (
            <div className="fd-intel-block fd-intel-standings">
              <p className="fd-intel-label">League table</p>
              <div className="fd-standings">
                {[
                  { side: standings.home, team: home, tag: "H" },
                  { side: standings.away, team: away, tag: "A" },
                ].map(({ side, team, tag }) => (
                  <div key={tag} className="fd-standing-row">
                    <span className="fd-standing-tag">{tag}</span>
                    <span className="fd-standing-team" title={team.name}>
                      {team.name || "—"}
                    </span>
                    <span className="fd-standing-stat">
                      <em>Rank</em>
                      <strong>{side?.rank != null ? `#${side.rank}` : "—"}</strong>
                    </span>
                    <span className="fd-standing-stat">
                      <em>Pts</em>
                      <strong className="tabular-nums">{side?.points ?? "—"}</strong>
                    </span>
                    <FormDots form={side?.form} />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default FixtureDeskIntel;
